import { Chip, Collapse, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

/**
 * One group (or leaf) in the sidebar navigation.
 *
 * Shared by BOTH UI templates — the classic left sidebar and the modern shell's
 * slide-out drawer both render it via NavMenu. Restyle here and both move together,
 * which is deliberate: a forked nav is how the two shells drifted apart before.
 *
 * Structure and behaviour are unchanged from the original; this is presentation only.
 *
 * The rhythm: rows are full-bleed with a 3px leading accent that is transparent until
 * the row is active. Reserving the accent at all times is what stops the label
 * jumping sideways when you navigate — the usual bug with a "border on active" nav.
 * `borderInlineStart` rather than `borderLeft` so it flips correctly in Arabic.
 */

// Leading accent + comfortable hit area, shared by group headers and child rows.
//
// ALL typography lives here, on the row, and never on the ListItemText. Both this and
// the `.active` rules below target `& .MuiTypography-root`, so putting one of them on
// the child element would make the two selectors equally specific and leave the winner
// down to emotion's injection order — the active row would take the right colour only
// by luck. Same element for both means `.active` (one class more) always wins.
const rowBase = {
  minHeight: 40,
  py: "7px",
  borderRadius: 0,
  borderInlineStart: "3px solid transparent",
  transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
  "& .MuiListItemText-root": { my: 0 },
  "& .MuiTypography-root": {
    fontSize: "13.5px",
    fontWeight: 500,
    color: "#475569",
    lineHeight: 1.35,
  },
  "&:hover": { backgroundColor: "var(--primarySM-color)" },
  // Keyboard users get a visible ring; mouse users never see it.
  "&.Mui-focusVisible": {
    backgroundColor: "var(--primarySM-color)",
    outline: "2px solid var(--primary-color)",
    outlineOffset: "-2px",
  },
};

// Children sit one level down in the hierarchy, so they sit one step back in weight
// and colour too. Merged into the row, not the label, for the reason above.
const childText = {
  "& .MuiTypography-root": {
    fontSize: "13px",
    fontWeight: 400,
    color: "#64748b",
    lineHeight: 1.35,
  },
};

const rowSelected = {
  backgroundColor: "var(--white-color)",
  borderInlineStartColor: "var(--primary-color)",
  "& .MuiListItemIcon-root": { color: "var(--primaryDark-color)" },
  "& .MuiTypography-root": { color: "var(--primaryDark-color)", fontWeight: 600 },
  "&:hover": { backgroundColor: "var(--white-color)" },
};

const iconSx = {
  minWidth: 34,
  color: "var(--gray-color)",
  "& svg": { fontSize: 19 },
};

const NavItem = (props) => {
  const { icon, path, title, child = [] } = props;
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const hasChildren = child.length > 0;
  const matches = (p) => !!p && location.pathname.includes(p);

  // A group reads as active when the current page lives inside it — whether or not it
  // happens to be expanded. Previously the header only lit up while OPEN, so on a fresh
  // load every group looked identical and the nav could not answer "where am I".
  const isSelected = hasChildren ? child.some((c) => matches(c.path)) : matches(path);

  useEffect(() => {
    if (hasChildren && !child.some((j) => location.pathname.includes(j.path))) {
      setOpen(false);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Leaf: a plain link row.
  if (!hasChildren) {
    return (
      <div className="mb-[2px]">
        {/* NavLink only — the original also called navigate() from onClick, which fired
            on Ctrl/⌘-click too, so the browser opened a new tab AND the current tab
            navigated. Letting NavLink own the click fixes that. */}
        <ListItemButton
          component={NavLink}
          to={path}
          sx={{ ...rowBase, px: "12px", "&.active": rowSelected }}
        >
          <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
          <ListItemText primary={title} />
        </ListItemButton>
      </div>
    );
  }

  // Group: header toggles, children collapse below it.
  return (
    <div className="mb-[2px]">
      <ListItemButton
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ ...rowBase, px: "12px", ...(isSelected ? rowSelected : null) }}
      >
        <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
        <ListItemText primary={title} />
        {/* One icon that rotates, rather than swapping two — the motion reads as the
            section opening instead of the glyph being replaced. */}
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 18,
            color: isSelected ? "var(--primaryDark-color)" : "var(--gray-color)",
            transition: "transform 160ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </ListItemButton>

      <Collapse in={open} timeout={160} unmountOnExit>
        <div className="pt-[2px] pb-[4px]">
          {child.map((item) => (
            // Indented to sit under the parent's LABEL, not its icon, so the group
            // reads as one block. The old arrow glyph on every row is gone — with
            // eight of them stacked it was noise, and indentation already says "child".
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              sx={{
                ...rowBase,
                ...childText,
                minHeight: 34,
                py: "5px",
                // Logical properties, not MUI's pl/pr shorthand: these flip on their
                // own in Arabic without depending on the RTL style plugin.
                paddingInlineStart: "46px",
                paddingInlineEnd: "12px",
                "&.active": rowSelected,
              }}
            >
              <ListItemText primary={item.title} />
              {/* Optional count, e.g. stock requests waiting on this branch. Rendered
                  only for a positive number so a quiet module shows nothing at all —
                  a grey "0" is worse than no badge. The count is supplied by
                  menuConfig behind the SAME access gate as the row itself; a badge on
                  a row you cannot open would advertise data you cannot see. */}
              {item.badge > 0 && (
                <Chip
                  size="small"
                  label={item.badge > 99 ? "99+" : item.badge}
                  sx={{
                    height: 18,
                    minWidth: 18,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "var(--primary-color)",
                    color: "#fff",
                    "& .MuiChip-label": { px: "6px" },
                  }}
                />
              )}
            </ListItemButton>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

export default NavItem;
